import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Menu as MenuIcon,
  Tag,
  X,
  Eye,
  EyeOff,
  Grid,
  List,
  MoreVertical,
  Copy,
  Archive,
  Settings,
} from 'lucide-react';

const Menus = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const queryClient = useQueryClient();

  const { data: menus, isLoading } = useQuery(
    ['menus'],
    async () => {
      const response = await api.get('/vendor/menus');
      return response.data;
    }
  );

  const { data: categories } = useQuery(
    ['menu-categories', selectedMenu?.id],
    async () => {
      if (!selectedMenu) return [];
      const response = await api.get(`/vendor/menus/${selectedMenu.id}/categories`);
      return response.data;
    },
    { enabled: !!selectedMenu }
  );

  const createMenuMutation = useMutation(
    async (menuData) => {
      const response = await api.post('/vendor/menus', menuData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menus']);
        showSuccessToast('Menu created successfully!', 'menu-create');
        setShowCreateModal(false);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to create menu', 'menu-create');
      },
    }
  );

  const updateMenuMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/vendor/menus/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menus']);
        showSuccessToast('Menu updated successfully!', 'menu-update');
        setEditingMenu(null);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update menu', 'menu-update');
      },
    }
  );

  const deleteMenuMutation = useMutation(
    async (id) => {
      await api.delete(`/vendor/menus/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menus']);
        showSuccessToast('Menu deleted successfully!', 'menu-delete');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to delete menu', 'menu-delete');
      },
    }
  );

  const toggleMenuStatusMutation = useMutation(
    async (id) => {
      await api.patch(`/vendor/menus/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menus']);
        showSuccessToast('Menu status updated!', 'menu-status');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update menu status', 'menu-status');
      },
    }
  );

  const createCategoryMutation = useMutation(
    async (categoryData) => {
      const response = await api.post(`/vendor/menus/${selectedMenu.id}/categories`, categoryData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-categories']);
        showSuccessToast('Category created successfully!', 'category-create');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to create category', 'category-create');
      },
    }
  );

  const updateCategoryMutation = useMutation(
    async ({ categoryId, data }) => {
      const response = await api.put(`/vendor/menus/${selectedMenu.id}/categories/${categoryId}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-categories']);
        showSuccessToast('Category updated successfully!', 'category-update');
        setEditingCategory(null);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update category', 'category-update');
      },
    }
  );

  const deleteCategoryMutation = useMutation(
    async (categoryId) => {
      await api.delete(`/vendor/menus/${selectedMenu.id}/categories/${categoryId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-categories']);
        showSuccessToast('Category deleted successfully!', 'category-delete');
        setDeletingCategory(null);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to delete category', 'category-delete');
        setDeletingCategory(null);
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const menuData = {
      type: formData.get('type'),
      is_active: formData.get('is_active') === 'true',
    };

    if (editingMenu) {
      updateMenuMutation.mutate({ id: editingMenu.id, data: menuData });
    } else {
      createMenuMutation.mutate(menuData);
    }
  };

  const handleCategorySubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const categoryData = {
      name: formData.get('name'),
      description: formData.get('description'),
      sort_order: parseInt(formData.get('sort_order')) || 0,
    };

    createCategoryMutation.mutate(categoryData);
    e.target.reset();
  };

  const handleCategoryUpdate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const categoryData = {
      name: formData.get('name'),
      description: formData.get('description'),
      sort_order: parseInt(formData.get('sort_order')) || 0,
    };

    updateCategoryMutation.mutate({ categoryId: editingCategory.id, data: categoryData });
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
  };

  const filteredMenus = menus?.filter(menu => {
    const matchesSearch = menu.type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && menu.is_active) ||
      (filterStatus === 'inactive' && !menu.is_active);
    return matchesSearch && matchesStatus;
  }) || [];

  const MenuModal = ({ menu = null, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {menu ? 'Edit Menu' : 'Create New Menu'}
          </h2>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Menu Type
            </label>
            <select
              name="type"
              defaultValue={menu?.type || ''}
              required
              className="input-field"
            >
              <option value="">Select menu type</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="dessert">Dessert</option>
              <option value="beverages">Beverages</option>
              <option value="appetizers">Appetizers</option>
              <option value="main-course">Main Course</option>
              <option value="sides">Sides</option>
              <option value="all">All</option>
            </select>
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                value="true"
                defaultChecked={menu?.is_active ?? true}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-secondary-700">Active Menu</span>
            </label>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={createMenuMutation.isLoading || updateMenuMutation.isLoading}
            >
              {createMenuMutation.isLoading || updateMenuMutation.isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                menu ? 'Update Menu' : 'Create Menu'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const CategoriesModal = ({ onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            Manage Categories - {selectedMenu?.type}
          </h2>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Add/Edit Category Form */}
          <div className="card">
            <h3 className="text-lg font-medium mb-4">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h3>
            {editingCategory && (
              <p className="text-sm text-secondary-600 mb-4">
                Editing: <span className="font-medium">{editingCategory.name}</span>
              </p>
            )}
            <form onSubmit={editingCategory ? handleCategoryUpdate : handleCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="input-field"
                  placeholder="e.g., Main Dishes"
                  defaultValue={editingCategory?.name || ''}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  rows="3"
                  className="input-field"
                  placeholder="Category description"
                  defaultValue={editingCategory?.description || ''}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  name="sort_order"
                  min="0"
                  className="input-field"
                  placeholder="0"
                  defaultValue={editingCategory?.sort_order || 0}
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}
                >
                  {createCategoryMutation.isLoading || updateCategoryMutation.isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    editingCategory ? 'Update Category' : 'Add Category'
                  )}
                </button>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="btn-secondary flex-1"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>
          
          {/* Categories List */}
          <div className="card">
            <h3 className="text-lg font-medium mb-4">Current Categories</h3>
            {categories?.length > 0 ? (
              <div className="space-y-3">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className={`p-3 rounded-lg transition-colors ${
                      editingCategory?.id === category.id 
                        ? 'bg-primary-50 border-2 border-primary-200' 
                        : 'bg-secondary-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-secondary-900">{category.name}</p>
                        {category.description && (
                          <p className="text-sm text-secondary-600 mt-1">{category.description}</p>
                        )}
                        <p className="text-xs text-secondary-500 mt-1">Order: {category.sort_order}</p>
                      </div>
                                          <div className="flex items-center space-x-2 ml-3">
                      {editingCategory?.id === category.id ? (
                        <span className="text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded-full">
                          Editing
                        </span>
                      ) : (
                        <button
                          onClick={() => setEditingCategory(category)}
                          className="text-primary-600 hover:text-primary-700 p-1 rounded"
                          title="Edit Category"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                        <button
                          onClick={() => setDeletingCategory(category)}
                          className="text-red-600 hover:text-red-700 p-1 rounded"
                          title="Delete Category"
                          disabled={deleteCategoryMutation.isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-secondary-600 text-center py-4">No categories yet</p>
            )}
          </div>
        </div>
        
        <div className="mt-6">
          <button
            onClick={onClose}
            className="btn-secondary w-full"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Menu Management</h1>
          <p className="text-secondary-600">
            Create and organize your restaurant menus and categories
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Menu
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                placeholder="Search menus..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Menus</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-secondary-400'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-secondary-400'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Menus Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMenus.map((menu) => (
            <div key={menu.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <MenuIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => toggleMenuStatusMutation.mutate(menu.id)}
                    className={`p-1 rounded ${menu.is_active ? 'text-green-600' : 'text-gray-400'}`}
                    title={menu.is_active ? 'Active' : 'Inactive'}
                  >
                    {menu.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <div className="relative">
                    <button className="p-1 rounded text-secondary-400 hover:text-secondary-600">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-secondary-900 mb-2 capitalize">
                {menu.type} Menu
              </h3>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary-600">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    menu.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {menu.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary-600">Items:</span>
                  <span className="font-medium">{menu.menuItems?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary-600">Categories:</span>
                  <span className="font-medium">{menu.menuCategories?.length || 0}</span>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedMenu(menu);
                    setShowCategoriesModal(true);
                  }}
                  className="btn-secondary flex-1 text-sm"
                >
                  <Tag className="h-4 w-4 mr-1" />
                  Categories
                </button>
                <button
                  onClick={() => setEditingMenu(menu)}
                  className="btn-primary flex-1 text-sm"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200">
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Menu</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Items</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Categories</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMenus.map((menu) => (
                  <tr key={menu.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                          <MenuIcon className="h-4 w-4 text-primary-600" />
                        </div>
                        <span className="font-medium capitalize">{menu.type} Menu</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        menu.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {menu.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4">{menu.menuItems?.length || 0}</td>
                    <td className="py-3 px-4">{menu.menuCategories?.length || 0}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedMenu(menu);
                            setShowCategoriesModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-700"
                          title="Manage Categories"
                        >
                          <Tag className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingMenu(menu)}
                          className="text-secondary-600 hover:text-secondary-700"
                          title="Edit Menu"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleMenuStatusMutation.mutate(menu.id)}
                          className={`${menu.is_active ? 'text-green-600' : 'text-gray-400'} hover:text-gray-600`}
                          title={menu.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {menu.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => deleteMenuMutation.mutate(menu.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete Menu"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredMenus.length === 0 && (
        <div className="text-center py-12">
          <MenuIcon className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">No menus found</h3>
          <p className="text-secondary-600 mb-4">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first menu'
            }
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Menu
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <MenuModal onClose={() => setShowCreateModal(false)} />
      )}
      
      {editingMenu && (
        <MenuModal menu={editingMenu} onClose={() => setEditingMenu(null)} />
      )}
      
      {showCategoriesModal && selectedMenu && (
        <CategoriesModal onClose={() => {
          setShowCategoriesModal(false);
          setEditingCategory(null);
        }} />
      )}

      {/* Delete Confirmation Modal */}
      {deletingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">Delete Category</h3>
              <button
                onClick={() => setDeletingCategory(null)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <p className="text-secondary-600 mb-6">
              Are you sure you want to delete the category "{deletingCategory.name}"? 
              This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => deleteCategoryMutation.mutate(deletingCategory.id)}
                className="btn-danger flex-1"
                disabled={deleteCategoryMutation.isLoading}
              >
                {deleteCategoryMutation.isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Delete Category'
                )}
              </button>
              <button
                onClick={() => setDeletingCategory(null)}
                className="btn-secondary flex-1"
                disabled={deleteCategoryMutation.isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Menus; 